<script setup>
import {
  ref,
  shallowRef,
  reactive,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import { useAlert } from 'dashboard/composables';
import SessionMessagePolicyPanel from '../../../../../components/SessionMessagePolicyPanel.vue';
import QuePasaIgnoredRecipientsPanel from '../../../../../components/QuePasaIgnoredRecipientsPanel.vue';
import TriStateField from '../../../../../components/TriStateField.vue';
import {
  useQuepasaChannelApi,
  castTristate,
} from '../../../../../composables/useQuepasaChannelApi';
import { getStateTextClass, normalizeState } from '../../../../../stateColors';
import { getSessionActionPolicy } from '../../../../../sessionActionPolicy';
import {
  applyInboxStatePayload,
  inboxStateSnapshots,
} from '../../../../../connectionStateStore';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const route = useRoute();
const { endpoint, http } = useQuepasaChannelApi(() => props.inbox);

const requestedConnectionPanel = () => {
  const panel = route.query?.panel;
  return Array.isArray(panel) ? panel[0] : panel;
};

// ---------------------------------------------------------------------------
// Connection state
// ---------------------------------------------------------------------------
const connectionState = ref('');
const connectedWid = ref('');
const stateTextClass = computed(() => getStateTextClass(connectionState.value));
const isSessionConnected = computed(() =>
  ['ready', 'connected'].includes(normalizeState(connectionState.value))
);

const quepasaVersion = ref('');
const lastCheckedAt = ref('');
const metaCoexistence = ref(null);
let stateRevision = 0;
let latestStatusRequest = 0;
let statusPollTimer = null;
const isFetchingConnectionStatus = ref(false);
const STATUS_POLL_INTERVAL_MS = 15000;

const applyConnectionSnapshot = snapshot => {
  if (!snapshot) return;
  if (snapshot.state) connectionState.value = snapshot.state;
  if (Object.prototype.hasOwnProperty.call(snapshot, 'wid')) {
    connectedWid.value = snapshot.wid;
  }
  if (snapshot.version) quepasaVersion.value = snapshot.version;
  if (snapshot.checkedAt) lastCheckedAt.value = snapshot.checkedAt;
  if (Object.prototype.hasOwnProperty.call(snapshot, 'metaCoexistence')) {
    metaCoexistence.value = snapshot.metaCoexistence;
  }
};

const applyConnectionPayload = payload => {
  stateRevision += 1;
  const snapshot = applyInboxStatePayload({
    ...payload,
    inbox_id: props.inbox?.id,
  });
  applyConnectionSnapshot(snapshot);
};

watch(
  () => inboxStateSnapshots[props.inbox?.id],
  snapshot => applyConnectionSnapshot(snapshot),
  { immediate: true }
);

const fetchConnectionStatus = async () => {
  if (isFetchingConnectionStatus.value) return;

  isFetchingConnectionStatus.value = true;
  latestStatusRequest += 1;
  const requestId = latestStatusRequest;
  const requestStateRevision = stateRevision;
  try {
    const { data } = await http.get(endpoint('/pair/status'));
    if (
      requestId !== latestStatusRequest ||
      requestStateRevision !== stateRevision
    ) {
      return;
    }

    applyConnectionPayload({
      state: data?.state || '',
      wid: data?.wid || '',
      meta_coexistence:
        typeof data?.meta_coexistence === 'boolean'
          ? data.meta_coexistence
          : null,
      health: {
        ...(data?.health || {}),
        timestamp: data?.health?.timestamp || new Date().toISOString(),
      },
    });
  } catch (error) {
    if (
      requestId !== latestStatusRequest ||
      requestStateRevision !== stateRevision
    ) {
      return;
    }
    applyConnectionPayload({
      state: 'Unreachable',
      checked_at: new Date().toISOString(),
    });
  } finally {
    isFetchingConnectionStatus.value = false;
  }
};

const startStatusPolling = () => {
  if (statusPollTimer) return;
  statusPollTimer = window.setInterval(
    fetchConnectionStatus,
    STATUS_POLL_INTERVAL_MS
  );
};

const stopStatusPolling = () => {
  if (statusPollTimer) window.clearInterval(statusPollTimer);
  statusPollTimer = null;
};

const onVisibilityChanged = () => {
  if (!document.hidden) fetchConnectionStatus();
};

const formattedWid = computed(() => {
  const digits = (connectedWid.value || '').split('@')[0]?.split(':')[0] || '';
  return digits ? `+${digits}` : '';
});
const widSection = computed(() => {
  const beforeAt = (connectedWid.value || '').split('@')[0] || '';
  const section = beforeAt.split(':')[1];
  return section || '-';
});

const onStateChanged = event => {
  const detail = event?.detail || {};
  if (Number(detail.inbox_id) !== Number(props.inbox?.id)) return;
  applyConnectionPayload(detail);
};

// ---------------------------------------------------------------------------
// QR code
// ---------------------------------------------------------------------------
const showQrPanel = ref(false);
const qrBase64 = ref('');
const qrError = ref('');
const isLoadingQr = ref(false);

const loadQrCode = async () => {
  isLoadingQr.value = true;
  qrError.value = '';
  try {
    const { data } = await http.get(endpoint('/pair'));
    qrBase64.value = data?.qr_base64 || '';
    if (data?.state) applyConnectionPayload({ state: data.state });
    if (!qrBase64.value) {
      qrError.value =
        data?.error || t('QUEPASA_CHANNEL.SETTINGS.PAIRING.QR_UNAVAILABLE');
    }
  } catch (error) {
    qrError.value =
      error.response?.data?.error ||
      t('QUEPASA_CHANNEL.SETTINGS.PAIRING.QR_UNAVAILABLE');
  } finally {
    isLoadingQr.value = false;
  }
};

const toggleQrPanel = async () => {
  showQrPanel.value = !showQrPanel.value;
  if (showQrPanel.value && !qrBase64.value) await loadQrCode();
};

// ---------------------------------------------------------------------------
// Pairing code (with ready-state polling)
// ---------------------------------------------------------------------------
const showPairPanel = ref(false);
const pairPhone = ref('');
const pairCode = ref('');
const pairingError = ref('');
const pairingDiagnostic = ref(null);
const isRequestingPairCode = ref(false);
const isWaitingConnection = ref(false);
let pairPollTimer = null;
let pairPollAttempts = 0;
let pairingRestrictionTimer = null;
const pairingRestrictionNow = ref(Date.now());
const PAIR_POLL_INTERVAL_MS = 5000;
const PAIR_POLL_MAX_ATTEMPTS = 36; // 3 minutes

const phoneFromWid = wid => {
  const user =
    String(wid || '')
      .split('@')[0]
      ?.split(':')[0] || '';
  const digits = user.replace(/\D/g, '');
  return digits.length >= 8 ? digits : '';
};

// Reuse the connected session number as the default pairing target. Keep this
// one-way: once the operator edits the field, later status refreshes must not
// overwrite that explicit choice.
watch(
  connectedWid,
  wid => {
    if (!pairPhone.value) pairPhone.value = phoneFromWid(wid);
  },
  { immediate: true }
);

const stopPairPolling = () => {
  if (pairPollTimer) clearInterval(pairPollTimer);
  pairPollTimer = null;
  isWaitingConnection.value = false;
};

const stopPairingRestrictionTimer = () => {
  if (pairingRestrictionTimer) clearInterval(pairingRestrictionTimer);
  pairingRestrictionTimer = null;
};

const pairingRestrictionExpiresAt = computed(() => {
  const value = pairingDiagnostic.value?.restriction_expires_at;
  if (!value) return null;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
});

const pairingRestrictionExpiresAtText = computed(() => {
  const expiresAt = pairingRestrictionExpiresAt.value;
  if (!expiresAt) return '';

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(expiresAt);
});

const pairingRestrictionRemaining = computed(() => {
  const expiresAt = pairingRestrictionExpiresAt.value;
  const now = pairingRestrictionNow.value;
  if (!expiresAt) return '';

  const remainingMilliseconds = expiresAt.getTime() - now;
  if (remainingMilliseconds <= 0) {
    return t('QUEPASA_CHANNEL.SETTINGS.PAIRING.RESTRICTION_EXPIRED');
  }

  const remainingMinutes = Math.ceil(remainingMilliseconds / 60000);
  const hours = Math.floor(remainingMinutes / 60);
  const minutes = remainingMinutes % 60;
  return t('QUEPASA_CHANNEL.SETTINGS.PAIRING.RESTRICTION_REMAINING', {
    hours,
    minutes,
  });
});

const startPairingRestrictionTimer = () => {
  stopPairingRestrictionTimer();
  if (!pairingRestrictionExpiresAt.value) return;

  pairingRestrictionTimer = setInterval(() => {
    pairingRestrictionNow.value = Date.now();
  }, 60000);
};

const clearPairingError = () => {
  pairingError.value = '';
  pairingDiagnostic.value = null;
  stopPairingRestrictionTimer();
};

const setPairingError = payload => {
  pairingError.value =
    payload?.error || t('QUEPASA_CHANNEL.SETTINGS.PAIRING.CODE_UNAVAILABLE');
  pairingDiagnostic.value = payload?.diagnostic || null;
  pairingRestrictionNow.value = Date.now();
  startPairingRestrictionTimer();
};

watch(isSessionConnected, connected => {
  if (!connected) return;

  stopPairPolling();
  pairCode.value = '';
  clearPairingError();
  qrBase64.value = '';
  showPairPanel.value = false;
  showQrPanel.value = false;
});

const startPairPolling = () => {
  stopPairPolling();
  pairPollAttempts = 0;
  isWaitingConnection.value = true;
  pairPollTimer = setInterval(async () => {
    pairPollAttempts += 1;
    await fetchConnectionStatus();
    if (isSessionConnected.value) {
      stopPairPolling();
      pairCode.value = '';
      qrBase64.value = '';
      showPairPanel.value = false;
      showQrPanel.value = false;
      useAlert(t('QUEPASA_CHANNEL.SETTINGS.PAIRING.CONNECTED'));
    } else if (pairPollAttempts >= PAIR_POLL_MAX_ATTEMPTS) {
      stopPairPolling();
      useAlert(t('QUEPASA_CHANNEL.SETTINGS.PAIRING.TIMEOUT'));
    }
  }, PAIR_POLL_INTERVAL_MS);
};

const requestPairCode = async () => {
  if (!pairPhone.value) return;
  isRequestingPairCode.value = true;
  clearPairingError();
  try {
    const { data } = await http.post(endpoint('/pair/code'), {
      phone: pairPhone.value,
    });
    pairCode.value = data?.code || '';
    if (pairCode.value) {
      clearPairingError();
      startPairPolling();
    } else {
      setPairingError(data);
      useAlert(pairingError.value);
    }
  } catch (error) {
    const payload = error.response?.data || { error: error.message };
    setPairingError(payload);
    useAlert(pairingError.value);
  } finally {
    isRequestingPairCode.value = false;
  }
};

// ---------------------------------------------------------------------------
// Session controls
// ---------------------------------------------------------------------------
const isRunningCommand = ref('');

const runControl = async (command, args) => {
  isRunningCommand.value = command;
  try {
    const payload = { command };
    if (args !== undefined) payload.arguments = args;
    const { data } = await http.post(endpoint('/control'), payload);
    if (data?.error) {
      useAlert(data.error);
    } else {
      useAlert(
        t('QUEPASA_CHANNEL.SETTINGS.MESSAGES.COMMAND_EXECUTED', { command })
      );
    }
    await fetchConnectionStatus();
    return data;
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
    return null;
  } finally {
    isRunningCommand.value = '';
  }
};

const sessionActionPolicy = computed(() =>
  getSessionActionPolicy(connectionState.value)
);
const canStartSession = computed(() => sessionActionPolicy.value.canStart);
const canStopSession = computed(() => sessionActionPolicy.value.canStop);
const canRestartSession = computed(() => sessionActionPolicy.value.canRestart);
const sessionActionHint = computed(() => {
  if (sessionActionPolicy.value.isServiceUnavailable) {
    return t('QUEPASA_CHANNEL.SETTINGS.SESSION.CONTROLS_UNREACHABLE');
  }
  if (sessionActionPolicy.value.isTransitioning) {
    return t('QUEPASA_CHANNEL.SETTINGS.SESSION.CONTROLS_TRANSITIONING');
  }
  if (sessionActionPolicy.value.requiresPairing) {
    return t('QUEPASA_CHANNEL.SETTINGS.SESSION.CONTROLS_PAIRING');
  }
  return '';
});
const isLoadingDiagnostics = ref(false);
const diagnosticsResult = ref(null);
const diagnosticsMessage = computed(() => {
  if (!diagnosticsResult.value) return '';
  if (diagnosticsResult.value.error) return diagnosticsResult.value.error;

  const state =
    diagnosticsResult.value.quepasa?.state ||
    connectionState.value ||
    t('QUEPASA_CHANNEL.SETTINGS.STATES.UNKNOWN');

  return t('QUEPASA_CHANNEL.SETTINGS.SESSION.DIAGNOSTICS_RESULT', { state });
});

const fetchDiagnostics = async () => {
  isLoadingDiagnostics.value = true;
  try {
    const { data } = await http.get(endpoint('/diagnostics'));
    diagnosticsResult.value = data;
    const diagnosticsState = data?.quepasa?.state;
    if (diagnosticsState) {
      applyConnectionPayload({
        state: diagnosticsState,
        checked_at: new Date().toISOString(),
      });
    }
    if (data?.error) useAlert(data.error);
  } catch (error) {
    const message = error.response?.data?.error || error.message;
    diagnosticsResult.value = { error: message };
    useAlert(message);
  } finally {
    isLoadingDiagnostics.value = false;
  }
};

// ---------------------------------------------------------------------------
// Configuration (options)
// ---------------------------------------------------------------------------
const quepasaUrl = ref('');
const isSavingOptions = ref(false);
const savingSwitch = ref('');
const optionsForm = reactive({
  outbound_enabled: true,
  typing_enabled: false,
  prepend_agent_name: false,
  historysyncdays: '',
  evaluation_message: '',
  call_message_content: '',
});
const sessionToggles = reactive({
  messages: null,
  direct: null,
  groups: null,
  broadcasts: null,
  calls: null,
  readreceipts: null,
  deliveryreceipts: null,
  readupdate: null,
});
const contextId = shallowRef('');
const isSavingContextId = shallowRef(false);
const messagePolicy = reactive({
  effective: true,
  source: 'default',
  locked: false,
});
const legacyToggleAttributeKeys = {
  readreceipts: 'read_receipts_enabled',
  readupdate: 'read_update_enabled',
};
const savingToggle = ref('');
const SESSION_TOGGLE_KEYS = [
  'messages',
  'direct',
  'groups',
  'broadcasts',
  'calls',
  'readreceipts',
  'deliveryreceipts',
  'readupdate',
];
const messageToggleItems = computed(() => [
  {
    key: 'direct',
    section: 'types',
    title: t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TOGGLES.DIRECT'),
    help: t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TOGGLES_HELP.DIRECT'),
    value: sessionToggles.direct,
  },
  {
    key: 'groups',
    section: 'types',
    title: t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TOGGLES.GROUPS'),
    help: t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TOGGLES_HELP.GROUPS'),
    value: sessionToggles.groups,
  },
  {
    key: 'broadcasts',
    section: 'types',
    title: t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TOGGLES.BROADCASTS'),
    help: t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TOGGLES_HELP.BROADCASTS'),
    value: sessionToggles.broadcasts,
  },
  {
    key: 'readreceipts',
    section: 'receipts',
    title: t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TOGGLES.READRECEIPTS'),
    help: t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TOGGLES_HELP.READRECEIPTS'),
    value: sessionToggles.readreceipts,
  },
  {
    key: 'deliveryreceipts',
    section: 'receipts',
    title: t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TOGGLES.DELIVERYRECEIPTS'),
    help: t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TOGGLES_HELP.DELIVERYRECEIPTS'),
    value: sessionToggles.deliveryreceipts,
  },
  {
    key: 'readupdate',
    section: 'receipts',
    title: t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TOGGLES.READUPDATE'),
    help: t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TOGGLES_HELP.READUPDATE'),
    value: sessionToggles.readupdate,
  },
]);
const independentSessionToggleItems = computed(() => [
  {
    key: 'calls',
    title: t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TOGGLES.CALLS'),
    help: t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TOGGLES_HELP.CALLS'),
    value: sessionToggles.calls,
  },
]);
const triStateLabels = computed(() => ({
  disabled: t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TOGGLE_STATES.DISABLED'),
  notConfigured: t(
    'QUEPASA_CHANNEL.SETTINGS.OPTIONS.TOGGLE_STATES.NOT_CONFIGURED'
  ),
  enabled: t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TOGGLE_STATES.ENABLED'),
}));
const messagePolicyValue = computed(() => ({
  value: sessionToggles.messages,
  effective: messagePolicy.effective,
  source: messagePolicy.source,
  locked: messagePolicy.locked,
}));
const messageEffectiveText = computed(() =>
  messagePolicy.effective
    ? t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.MESSAGE_POLICY.EFFECTIVE_ENABLED')
    : t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.MESSAGE_POLICY.EFFECTIVE_DISABLED')
);

const applyConfig = (attrs, sessionPreferences = {}) => {
  optionsForm.outbound_enabled = castTristate(attrs.outbound_enabled) !== false;
  optionsForm.typing_enabled = castTristate(attrs.typing_enabled) === true;
  optionsForm.prepend_agent_name =
    castTristate(attrs.prepend_agent_name) === true;
  optionsForm.historysyncdays = attrs.historysyncdays ?? '';
  optionsForm.evaluation_message = attrs.evaluation_message || '';
  optionsForm.call_message_content = attrs.call_message_content || '';
  if (Object.prototype.hasOwnProperty.call(sessionPreferences, 'contextid')) {
    contextId.value = sessionPreferences.contextid || '';
  }
  SESSION_TOGGLE_KEYS.forEach(key => {
    const hasLiveValue = Object.prototype.hasOwnProperty.call(
      sessionPreferences,
      key
    );
    const attributeKey = `${key}_enabled`;
    const legacyAttributeKey = legacyToggleAttributeKeys[key];
    const attributeValue = Object.prototype.hasOwnProperty.call(
      attrs,
      attributeKey
    )
      ? attrs[attributeKey]
      : attrs[legacyAttributeKey];
    sessionToggles[key] = castTristate(
      hasLiveValue ? sessionPreferences[key] : attributeValue
    );
  });
  messagePolicy.effective =
    typeof sessionPreferences.messages_effective === 'boolean'
      ? sessionPreferences.messages_effective
      : sessionToggles.messages !== false;
  messagePolicy.source = sessionPreferences.messages_source || 'default';
  messagePolicy.locked = sessionPreferences.messages_locked === true;
};

// Typing works only when global (Super Admin), account and inbox levels are
// all enabled — the backend reports each level in GET /config.
const typingLevels = ref(null);

const fetchConfig = async () => {
  if (!props.inbox?.id) return;
  try {
    const { data } = await http.get(endpoint('/config'));
    quepasaUrl.value = data?.quepasa_url || '';
    typingLevels.value = data?.typing || null;
    applyConfig(
      data?.additional_attributes || {},
      data?.session_preferences || {}
    );
  } catch (error) {
    // Non-blocking: the section simply renders empty values.
  }
};

const typingLevelLabel = enabled =>
  enabled
    ? t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TYPING_LEVELS.ENABLED')
    : t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TYPING_LEVELS.DISABLED');
const typingLevelLines = computed(() => ({
  global: `${t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TYPING_LEVELS.GLOBAL')}: ${typingLevelLabel(typingLevels.value?.global)}`,
  account: `${t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TYPING_LEVELS.ACCOUNT')}: ${typingLevelLabel(typingLevels.value?.account)}`,
  inbox: `${t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TYPING_LEVELS.INBOX')}: ${typingLevelLabel(typingLevels.value?.inbox)}`,
}));

// Hierarchical gating: the inbox switch is only operable when BOTH parents
// (global AND account) are enabled. Parent off ⇒ child locked off.
const typingParentsEnabled = computed(
  () =>
    typingLevels.value?.global === true && typingLevels.value?.account === true
);

const saveSwitch = async key => {
  savingSwitch.value = key;
  try {
    const { data } = await http.post(endpoint('/config'), {
      config: { [key]: optionsForm[key] },
    });
    applyConfig(data?.additional_attributes || {});
    useAlert(t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.SAVED'));
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
  } finally {
    savingSwitch.value = '';
  }
};

const saveMessages = async () => {
  isSavingOptions.value = true;
  try {
    const config = {
      call_message_content: optionsForm.call_message_content,
    };
    const { data } = await http.post(endpoint('/config'), { config });
    applyConfig(data?.additional_attributes || {});
    useAlert(t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.SAVED'));
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
  } finally {
    isSavingOptions.value = false;
  }
};

// History sync days defines how much WhatsApp history is imported on the first
// connection via QR/pair code, so it must be set before pairing.
const isSavingHistory = ref(false);

const saveHistorySync = async () => {
  isSavingHistory.value = true;
  try {
    const { data } = await http.post(endpoint('/config'), {
      config: { historysyncdays: optionsForm.historysyncdays },
    });
    applyConfig(data?.additional_attributes || {});
    useAlert(t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.SAVED'));
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
  } finally {
    isSavingHistory.value = false;
  }
};

const saveContextId = async () => {
  if (isSavingContextId.value) return;

  isSavingContextId.value = true;
  try {
    const { data } = await http.post(endpoint('/config'), {
      config: { contextid: contextId.value.trim() },
    });
    applyConfig(
      data?.additional_attributes || {},
      data?.session_preferences || {}
    );
    useAlert(t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.SAVED'));
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
  } finally {
    isSavingContextId.value = false;
  }
};

const setSessionToggle = async (key, value) => {
  savingToggle.value = key;
  try {
    const payload = { command: key, arguments: value };
    const { data } = await http.post(endpoint('/control'), payload);
    if (data?.error) {
      useAlert(data.error);
    } else {
      await fetchConfig();
      useAlert(t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.SAVED'));
    }
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
  } finally {
    savingToggle.value = '';
  }
};

// ---------------------------------------------------------------------------
onMounted(() => {
  window.addEventListener('quepasa-channel:state-changed', onStateChanged);
  document.addEventListener('visibilitychange', onVisibilityChanged);
  fetchConfig();
  fetchConnectionStatus();
  startStatusPolling();
  const panel = requestedConnectionPanel();
  if (panel === 'qr') {
    showQrPanel.value = true;
    loadQrCode();
  } else if (panel === 'pairing') {
    showPairPanel.value = true;
  }
});

onBeforeUnmount(() => {
  stopPairPolling();
  stopStatusPolling();
  stopPairingRestrictionTimer();
  document.removeEventListener('visibilitychange', onVisibilityChanged);
  window.removeEventListener('quepasa-channel:state-changed', onStateChanged);
});
</script>

<template>
  <div class="mx-8">
    <!-- WhatsApp Connection Section -->
    <SettingsSection
      v-if="!isSessionConnected"
      :title="t('QUEPASA_CHANNEL.SETTINGS.CONNECTION.TITLE')"
      :sub-title="t('QUEPASA_CHANNEL.SETTINGS.CONNECTION.SUBTITLE')"
    >
      <div class="flex flex-col gap-4">
        <!-- QR Code Accordion -->
        <div class="border border-n-slate-6 rounded-lg overflow-hidden">
          <button
            type="button"
            data-target="quepasa-channel-load-qr"
            class="flex items-center justify-between w-full p-4 text-left hover:bg-n-slate-2 transition-colors"
            @click="toggleQrPanel"
          >
            <div class="flex flex-col gap-1">
              <p class="mb-0 text-sm font-medium text-n-slate-12">
                {{ t('QUEPASA_CHANNEL.SETTINGS.QR.TITLE') }}
              </p>
              <p class="mb-0 text-xs text-n-slate-11">
                {{ t('QUEPASA_CHANNEL.SETTINGS.QR.METHOD_DESC') }}
              </p>
            </div>
            <fluent-icon
              :icon="showQrPanel ? 'chevron-up' : 'chevron-down'"
              size="20"
              class="text-n-slate-11"
            />
          </button>

          <div
            v-if="showQrPanel"
            data-target="quepasa-channel-qr-panel"
            class="p-4 border-t border-n-slate-6 bg-n-slate-1"
          >
            <div class="flex flex-col items-center gap-3">
              <p class="mb-3 text-sm text-center text-n-slate-11">
                {{ t('QUEPASA_CHANNEL.SETTINGS.QR.INSTRUCTIONS') }}
              </p>
              <div
                class="w-48 h-48 rounded-lg border border-n-slate-6 overflow-hidden flex items-center justify-center"
                :class="qrBase64 ? 'bg-white' : 'bg-n-slate-2 animate-pulse'"
              >
                <img
                  v-if="qrBase64"
                  data-target="quepasa-channel-qr-image"
                  :src="`data:image/png;base64,${qrBase64}`"
                  alt="QR Code"
                  class="w-full h-full object-contain p-2"
                />
                <span v-else class="text-n-slate-9 text-xs">
                  {{
                    isLoadingQr
                      ? t('QUEPASA_CHANNEL.SETTINGS.QR.LOADING')
                      : t('QUEPASA_CHANNEL.SETTINGS.QR.LOAD_ERROR')
                  }}
                </span>
              </div>
              <p
                v-if="qrError"
                class="mb-0 max-w-lg text-xs text-center text-rose-600"
              >
                {{ qrError }}
              </p>
              <NextButton
                icon="i-lucide-refresh-cw"
                :label="t('QUEPASA_CHANNEL.SETTINGS.PAIRING.REFRESH_QR')"
                :is-loading="isLoadingQr"
                @click="loadQrCode"
              />
            </div>
          </div>
        </div>

        <!-- Pairing Code Accordion -->
        <div class="border border-n-slate-6 rounded-lg overflow-hidden">
          <button
            type="button"
            data-target="quepasa-channel-pair-toggle"
            class="flex items-center justify-between w-full p-4 text-left hover:bg-n-slate-2 transition-colors"
            @click="showPairPanel = !showPairPanel"
          >
            <div class="flex flex-col gap-1">
              <p class="mb-0 text-sm font-medium text-n-slate-12">
                {{ t('QUEPASA_CHANNEL.SETTINGS.PAIRING.PAIR_TITLE') }}
              </p>
              <p class="mb-0 text-xs text-n-slate-11">
                {{ t('QUEPASA_CHANNEL.SETTINGS.PAIRING.METHOD_DESC') }}
              </p>
            </div>
            <fluent-icon
              :icon="showPairPanel ? 'chevron-up' : 'chevron-down'"
              size="20"
              class="text-n-slate-11"
            />
          </button>

          <div
            v-if="showPairPanel"
            class="p-4 border-t border-n-slate-6 bg-n-slate-1"
          >
            <div class="flex flex-col gap-4">
              <p class="mb-0 text-xs text-n-slate-11">
                {{ t('QUEPASA_CHANNEL.SETTINGS.PAIRING.DESCRIPTION') }}
              </p>
              <input
                v-model="pairPhone"
                data-target="quepasa-channel-pair-phone"
                type="text"
                :placeholder="
                  t('QUEPASA_CHANNEL.SETTINGS.PAIRING.PHONE_PLACEHOLDER')
                "
                class="px-3 py-2 text-sm border border-n-slate-6 rounded-md focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent"
                @keydown.enter.prevent="requestPairCode"
              />
              <NextButton
                data-target="quepasa-channel-pair-code"
                :label="t('QUEPASA_CHANNEL.SETTINGS.PAIRING.GET_CODE')"
                :is-loading="isRequestingPairCode"
                :disabled="!pairPhone"
                @click="requestPairCode"
              />
              <div
                v-if="pairingError"
                data-target="quepasa-channel-pairing-error"
                class="flex flex-col gap-1 p-3 rounded-md border border-rose-300 bg-rose-50 text-sm text-rose-700"
              >
                <p class="mb-0 font-medium">{{ pairingError }}</p>
                <p
                  v-if="pairingDiagnostic?.suggested_action"
                  class="mb-0 text-xs"
                >
                  {{
                    t('QUEPASA_CHANNEL.SETTINGS.PAIRING.RECOMMENDED_ACTION', {
                      action: pairingDiagnostic.suggested_action,
                    })
                  }}
                </p>
                <p v-if="pairingRestrictionExpiresAtText" class="mb-0 text-xs">
                  {{
                    t(
                      'QUEPASA_CHANNEL.SETTINGS.PAIRING.RESTRICTION_EXPIRES_AT',
                      {
                        at: pairingRestrictionExpiresAtText,
                      }
                    )
                  }}
                </p>
                <p
                  v-if="pairingRestrictionRemaining"
                  data-target="quepasa-channel-pairing-remaining"
                  class="mb-0 text-xs font-medium"
                >
                  {{ pairingRestrictionRemaining }}
                </p>
              </div>
              <div v-if="pairCode" class="flex flex-col items-center gap-3">
                <div
                  data-target="quepasa-channel-pair-code-value"
                  class="text-center text-3xl font-mono font-bold text-n-brand py-4 px-6 bg-n-brand/10 rounded-lg border border-n-brand/30 w-full"
                >
                  {{ pairCode }}
                </div>
                <div
                  v-if="isWaitingConnection"
                  class="flex items-center gap-2 text-xs text-n-slate-11"
                >
                  <span
                    class="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse"
                  />
                  {{ t('QUEPASA_CHANNEL.SETTINGS.PAIRING.WAITING') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SettingsSection>

    <!-- Session Info Section -->
    <SettingsSection
      :title="t('QUEPASA_CHANNEL.SETTINGS.SESSION.TITLE')"
      :sub-title="t('QUEPASA_CHANNEL.SETTINGS.SESSION.SUBTITLE')"
    >
      <div data-target="quepasa-channel-controls" class="flex flex-col gap-4">
        <div class="flex flex-col gap-4 sm:flex-row sm:items-start">
          <span
            v-if="metaCoexistence === true"
            data-target="quepasa-channel-meta-coexistence-badge"
            class="order-1 inline-flex w-fit shrink-0 items-center rounded-full border border-n-teal-5 bg-n-teal-3 px-2 py-1 text-xs font-medium text-n-teal-11 sm:order-2 sm:ml-auto"
          >
            {{ t('QUEPASA_CHANNEL.SETTINGS.SESSION.COEXISTENCE') }}
          </span>
          <div
            class="order-2 grid flex-1 grid-cols-1 gap-6 sm:order-1 sm:grid-cols-3"
          >
            <div>
              <label class="text-xs font-medium text-n-slate-11 uppercase mb-1">
                {{ t('QUEPASA_CHANNEL.SETTINGS.SESSION.CONNECTED_NUMBER') }}
              </label>
              <p class="mb-0 text-sm font-medium text-n-slate-12">
                {{
                  formattedWid ||
                  t('QUEPASA_CHANNEL.SETTINGS.SESSION.NOT_AVAILABLE')
                }}
              </p>
            </div>
            <div>
              <label class="text-xs font-medium text-n-slate-11 uppercase mb-1">
                {{ t('QUEPASA_CHANNEL.SETTINGS.SESSION.SECTION') }}
              </label>
              <p class="mb-0 text-sm font-medium text-n-slate-12">
                {{ widSection }}
              </p>
            </div>
            <div>
              <label class="text-xs font-medium text-n-slate-11 uppercase mb-1">
                {{ t('QUEPASA_CHANNEL.SETTINGS.SESSION.STATUS') }}
              </label>
              <p
                class="mb-0 text-sm font-medium"
                :class="stateTextClass"
                data-target="quepasa-channel-connection-state"
              >
                {{
                  connectionState ||
                  t('QUEPASA_CHANNEL.SETTINGS.STATES.UNKNOWN')
                }}
              </p>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-6 pt-4 border-t border-n-slate-6">
          <div>
            <label class="text-xs font-medium text-n-slate-11 uppercase mb-1">
              {{ t('QUEPASA_CHANNEL.SETTINGS.SESSION.VERSION') }}
            </label>
            <p class="mb-0 text-sm font-medium text-n-slate-12">
              {{
                quepasaVersion ||
                t('QUEPASA_CHANNEL.SETTINGS.SESSION.NOT_AVAILABLE')
              }}
            </p>
          </div>
          <div>
            <label class="text-xs font-medium text-n-slate-11 uppercase mb-1">
              {{ t('QUEPASA_CHANNEL.SETTINGS.SESSION.LAST_CHECK') }}
            </label>
            <p class="mb-0 text-sm font-medium text-n-slate-12">
              {{
                lastCheckedAt
                  ? new Date(lastCheckedAt).toLocaleString('pt-BR')
                  : t('QUEPASA_CHANNEL.SETTINGS.SESSION.NOT_AVAILABLE')
              }}
            </p>
          </div>
        </div>

        <!-- Session actions on their own row -->
        <div
          class="flex flex-wrap items-center gap-3 pt-4 border-t border-n-slate-6"
        >
          <p
            v-if="sessionActionHint"
            data-target="quepasa-channel-session-action-hint"
            class="flex-1 min-w-60 mb-0 text-sm text-n-slate-11"
          >
            {{ sessionActionHint }}
          </p>
          <NextButton
            v-if="canStartSession"
            icon="i-lucide-play"
            color-scheme="success"
            size="sm"
            data-target="quepasa-channel-start"
            :label="t('QUEPASA_CHANNEL.SETTINGS.ACTIONS.START')"
            :is-loading="isRunningCommand === 'start'"
            @click="runControl('start')"
          />
          <NextButton
            v-if="canStopSession"
            icon="i-lucide-circle-x"
            color-scheme="alert"
            size="sm"
            data-target="quepasa-channel-stop"
            :label="t('QUEPASA_CHANNEL.SETTINGS.ACTIONS.STOP')"
            :is-loading="isRunningCommand === 'stop'"
            @click="runControl('stop')"
          />
          <NextButton
            v-if="canRestartSession"
            icon="i-lucide-rotate-cw"
            color-scheme="secondary"
            size="sm"
            data-target="quepasa-channel-restart"
            :label="t('QUEPASA_CHANNEL.SETTINGS.ACTIONS.RESTART')"
            :is-loading="isRunningCommand === 'restart'"
            @click="runControl('restart')"
          />
          <NextButton
            icon="i-lucide-stethoscope"
            color-scheme="secondary"
            size="sm"
            data-target="quepasa-channel-diagnostics"
            :label="t('QUEPASA_CHANNEL.SETTINGS.ACTIONS.DIAGNOSTICS')"
            :is-loading="isLoadingDiagnostics"
            @click="fetchDiagnostics"
          />
          <NextButton
            class="ml-auto"
            icon="i-lucide-refresh-cw"
            color-scheme="secondary"
            size="sm"
            data-target="quepasa-channel-refresh-status"
            :label="t('QUEPASA_CHANNEL.SETTINGS.CONNECTION.REFRESH')"
            :is-loading="isFetchingConnectionStatus"
            @click="fetchConnectionStatus"
          />
        </div>
        <p
          v-if="diagnosticsMessage"
          data-target="quepasa-channel-diagnostics-result"
          class="mb-0 text-sm text-n-slate-11"
        >
          {{ diagnosticsMessage }}
        </p>
      </div>
    </SettingsSection>

    <!-- Prepend agent name switch -->
    <SettingsSection
      :title="t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.PREPEND_AGENT')"
    >
      <div class="flex flex-col gap-2">
        <Switch
          v-model="optionsForm.prepend_agent_name"
          @change="saveSwitch('prepend_agent_name')"
        />
        <p class="mb-0 text-sm text-n-slate-11">
          {{ t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.PREPEND_AGENT_HELP') }}
        </p>
        <NextButton
          v-if="savingSwitch === 'prepend_agent_name'"
          :label="t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.SAVE')"
          is-loading
        />
      </div>
    </SettingsSection>

    <!-- Message processing policy and dependent message features -->
    <SettingsSection
      :title="t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TOGGLES.MESSAGES')"
      content-class="xl:col-span-6"
    >
      <SessionMessagePolicyPanel
        :policy="messagePolicyValue"
        :items="messageToggleItems"
        :section-labels="{
          types: t(
            'QUEPASA_CHANNEL.SETTINGS.OPTIONS.MESSAGE_POLICY.TYPES_TITLE'
          ),
          receipts: t(
            'QUEPASA_CHANNEL.SETTINGS.OPTIONS.MESSAGE_POLICY.RECEIPTS_TITLE'
          ),
        }"
        :states="triStateLabels"
        :compact-states="{
          disabled: t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.DISABLED'),
          notConfigured: t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.NOT_CONFIGURED'),
          enabled: t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.ENABLED'),
        }"
        :help="t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TOGGLES_HELP.MESSAGES')"
        :effective-text="messageEffectiveText"
        :locked-text="
          t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.MESSAGE_POLICY.LOCKED')
        "
        :expand-label="
          t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.MESSAGE_POLICY.EXPAND')
        "
        :collapse-label="
          t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.MESSAGE_POLICY.COLLAPSE')
        "
        :loading-label="t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.SAVE')"
        :saving-key="savingToggle"
        target-prefix="quepasa-channel-message"
        @change="setSessionToggle"
      />
    </SettingsSection>

    <SettingsSection
      :title="t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.IGNORED_RECIPIENTS.TITLE')"
    >
      <QuePasaIgnoredRecipientsPanel :inbox="inbox" />
    </SettingsSection>

    <!-- Calls remain independent from message processing in QuePasa -->
    <SettingsSection
      v-for="toggle in independentSessionToggleItems"
      :key="toggle.key"
      :title="toggle.title"
    >
      <TriStateField
        :id-prefix="`qpc-${toggle.key}`"
        :value="toggle.value"
        :help="toggle.help"
        :states="triStateLabels"
        :loading="savingToggle === toggle.key"
        :loading-label="t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.SAVE')"
        :data-target="`quepasa-channel-toggle-${toggle.key}`"
        @change="setSessionToggle(toggle.key, $event)"
      />
    </SettingsSection>

    <SettingsSection
      :title="t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.CONTEXT_ID.LABEL')"
    >
      <div class="flex flex-col gap-2">
        <label
          for="quepasa-channel-context-id"
          class="text-sm font-medium text-n-slate-12"
        >
          {{ t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.CONTEXT_ID.LABEL') }}
        </label>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.CONTEXT_ID.HELP') }}
        </p>
        <input
          id="quepasa-channel-context-id"
          v-model="contextId"
          data-target="quepasa-channel-context-id"
          type="text"
          maxlength="255"
          autocomplete="off"
          class="px-3 py-2 text-sm border border-n-slate-6 rounded-md bg-n-slate-1 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent"
          :placeholder="
            t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.CONTEXT_ID.PLACEHOLDER')
          "
        />
        <NextButton
          class="self-start"
          size="sm"
          data-target="quepasa-channel-context-id-save"
          :label="t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.CONTEXT_ID.SAVE')"
          :is-loading="isSavingContextId"
          @click="saveContextId"
        />
      </div>
    </SettingsSection>

    <div
      data-target="quepasa-channel-advanced-options"
      class="my-6 overflow-hidden rounded-lg border border-n-slate-6 bg-n-slate-1"
    >
      <div class="p-4">
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.ADVANCED_TITLE') }}
        </p>
        <p class="mb-0 mt-1 text-xs text-n-slate-11">
          {{ t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.ADVANCED_SUBTITLE') }}
        </p>
      </div>

      <div class="border-t border-n-slate-6 px-4">
        <SettingsSection
          :title="t('QUEPASA_CHANNEL.SETTINGS.HISTORY_SYNC.TITLE')"
          :sub-title="t('QUEPASA_CHANNEL.SETTINGS.HISTORY_SYNC.DESCRIPTION')"
        >
          <div class="flex flex-col gap-2">
            <input
              v-model="optionsForm.historysyncdays"
              data-target="quepasa-channel-history-sync-days"
              type="text"
              class="px-3 py-2 text-sm border border-n-slate-6 rounded-md bg-n-slate-1 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent"
              :placeholder="
                t('QUEPASA_CHANNEL.SETTINGS.HISTORY_SYNC.PLACEHOLDER')
              "
            />
            <NextButton
              class="self-end"
              size="sm"
              data-target="quepasa-channel-history-sync-save"
              :label="t('QUEPASA_CHANNEL.SETTINGS.HISTORY_SYNC.SAVE')"
              :is-loading="isSavingHistory"
              @click="saveHistorySync"
            />
          </div>
        </SettingsSection>

        <SettingsSection
          :title="t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.OUTBOUND')"
        >
          <div class="flex flex-col gap-2">
            <Switch
              v-model="optionsForm.outbound_enabled"
              @change="saveSwitch('outbound_enabled')"
            />
            <p class="mb-0 text-sm text-n-slate-11">
              {{ t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.OUTBOUND_HELP') }}
            </p>
            <NextButton
              v-if="savingSwitch === 'outbound_enabled'"
              :label="t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.SAVE')"
              is-loading
            />
          </div>
        </SettingsSection>

        <SettingsSection :title="t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TYPING')">
          <div class="flex flex-col gap-2">
            <Switch
              v-model="optionsForm.typing_enabled"
              :disabled="!typingParentsEnabled"
              :class="{
                'opacity-50 pointer-events-none': !typingParentsEnabled,
              }"
              @change="saveSwitch('typing_enabled')"
            />
            <p class="mb-0 text-sm text-n-slate-11">
              {{ t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.TYPING_HELP') }}
            </p>
            <p
              v-if="typingLevels && !typingLevels.global"
              class="mb-0 text-sm text-amber-600"
            >
              {{
                t(
                  'QUEPASA_CHANNEL.SETTINGS.OPTIONS.TYPING_LEVELS.GLOBAL_DISABLED_WARNING'
                )
              }}
            </p>
            <div
              v-if="typingLevels"
              class="flex flex-col gap-1 text-xs text-n-slate-11"
            >
              <span>{{ typingLevelLines.global }}</span>
              <span>{{ typingLevelLines.account }}</span>
              <span>{{ typingLevelLines.inbox }}</span>
            </div>
            <NextButton
              v-if="savingSwitch === 'typing_enabled'"
              :label="t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.SAVE')"
              is-loading
            />
          </div>
        </SettingsSection>

        <SettingsSection
          :title="t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.MESSAGES_TITLE')"
          :sub-title="t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.MESSAGES_SUBTITLE')"
        >
          <div class="flex flex-col gap-4">
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{ t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.CALL_MESSAGE') }}
              <textarea
                v-model="optionsForm.call_message_content"
                rows="2"
                class="px-3 py-2 text-sm border border-n-slate-6 rounded-md focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent"
              />
            </label>
            <NextButton
              class="self-start"
              data-target="quepasa-channel-save-options"
              :label="t('QUEPASA_CHANNEL.SETTINGS.OPTIONS.SAVE')"
              :is-loading="isSavingOptions"
              @click="saveMessages"
            />
          </div>
        </SettingsSection>
      </div>
    </div>
  </div>
</template>
